import type { TableProps, TabsProps } from "antd";
import { Button, Input, Modal, Select, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LicenseState,
  PaymentStatus,
  PaymentStatusLabel,
} from "../../enum/lisence";
import requiredLabel from "../../images/required.png";
import searchIcon from "../../images/search_icon.png";
import closeModal from "../../images/close-modal-icon.png";
import { DataType } from "../../models/interface/license.interface";
import { api } from "../../utils/api";
import { PAGE, PAGE_SIZE } from "../../utils/constants/common";
import Tab1 from "./LisenceMangement/Tab1";
import Tab2 from "./LisenceMangement/Tab2";
import Tab3 from "./LisenceMangement/Tab3";
import { CSVIcon } from "../../components/Common/Icon";

const AdminLisenceManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = React.useState("");
  const [list, setList] = React.useState<DataType[]>([]);
  const [total, setTotal] = React.useState(0);
  const [listId, setListId] = React.useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [state, setState] = useState<string>("");

  const page = searchParams.get("page") || PAGE;
  const pageSize = searchParams.get("pageSize") || PAGE_SIZE;
  const tab = searchParams.get("tab") || LicenseState.UNASSIGNED;

  const getData = async () => {
    const res = await api.get(`api/licenses`, {
      params: {
        page: page,
        pageSize: pageSize,
        state: tab,
        search: searchTerm ?? undefined,
      },
    });
    setList(res.data.items);
    setTotal(res.data.total);
  };

  useEffect(() => {
    getData();
  }, [tab, page, pageSize]);

  const search = async () => {
    setSearchParams({ ...searchParams, search: searchTerm });
    const params = new URLSearchParams(searchParams);
    if (searchTerm) {
      params.set("search", searchTerm);
    } else {
      params.delete("search");
    }
    setSearchParams(params);
    getData();
  };

  const onChange = (key: string) => {
    setSearchParams({ tab: key });
    setSearchTerm("");
    setListId([]);
  };

  useEffect(() => {
    setSearchParams({ tab: LicenseState.UNASSIGNED });
  }, []);

  const rowSelection: TableProps<DataType>["rowSelection"] = {
    onChange: (_, selectedRows: DataType[]) => {
      setListId(selectedRows.map((item) => item.id));
    },
    selectedRowKeys: listId,
    hideSelectAll: true,
  };

  const openModalEditState = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    setIsModalOpen(false);
    const data = {
      ids: listId,
      newState: state,
    };
    await api.put(`api/licenses/payment-status`, data);
    getData();
    setListId([]);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleChangeState = (value: string) => {
    setState(value);
  };

  const items: TabsProps["items"] = [
    {
      key: LicenseState.UNASSIGNED,
      label: "在庫",
      children: <Tab1 data={list} total={total} />,
    },
    {
      key: LicenseState.REQUESTING,
      label: "申請中",
      children: <Tab2 data={list} total={total} />,
    },
    {
      key: LicenseState.ACTIVATED,
      label: "販売済",
      children: <Tab3 data={list} total={total} rowSelection={rowSelection} />,
    },
    {
      key: LicenseState.REVOKED,
      label: "廃棄",
      children: null,
    },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex justify-between items-center mb-4">
        <div className="w-fit flex">
          <Input
            placeholder="カメラSN・ハードウェアSN・代理店名で検索"
            className="h-11 w-[424px]"
            prefix={<img src={searchIcon} className="h-6" alt="search icon" />}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            value={searchTerm}
          />
          <Button
            onClick={() => search()}
            className="ml-2 h-[46px] w-[52px] base-button"
          >
            検索
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {listId.length > 0 && (
            <Button
              onClick={() => openModalEditState()}
              className="h-11 w-[150px] base-button"
            >
              決済ステータス更新
            </Button>
          )}
          <Button
            className="h-11 w-[137px] text-[#0077C8] text-xl font-medium border-[#0077C8]"
            icon={<CSVIcon />}
          >
            CSV出力
          </Button>
        </div>
      </div>

      <Tabs
        defaultActiveKey={LicenseState.UNASSIGNED}
        items={items}
        onChange={onChange}
        className="tabs-custom"
      />
      <Modal
        className="custom-modal"
        title="決済ステータス更新"
        closeIcon={<img src={closeModal} alt="close icon" className="w-6" />}
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={(_) => (
          <div className="flex gap-2 justify-between">
            <Button
              className="text-base font-medium h-[52px] flex-1"
              onClick={handleCancel}
            >
              キャンセル
            </Button>
            <Button className="h-[52px] flex-1 base-button" onClick={handleOk}>
              保存
            </Button>
          </div>
        )}
      >
        <div>
          <div className="flex gap-2 items-center mb-[6px]">
            <p className="mb-0 text-sm font-semibold">決済ステータス</p>
            <img src={requiredLabel} alt="Required" />
          </div>
          <Select
            placeholder="お支払いの状態を選択してください"
            style={{ width: "100%", height: "46px" }}
            onChange={handleChangeState}
            options={[
              { value: PaymentStatus.PAID, label: PaymentStatusLabel.paid },
              { value: PaymentStatus.UNPAID, label: PaymentStatusLabel.unpaid },
              {
                value: PaymentStatus.CANCELLED,
                label: PaymentStatusLabel.cancelled,
              },
            ]}
          />
        </div>
      </Modal>
    </div>
  );
};

export default AdminLisenceManagementPage;
